import React, { FC, useEffect, useState } from 'react';
import { classes, stylesheet } from 'typestyle';
import { CloseIcon, DeleteIcon, DownloadIcon } from '@traceair/webapp-icons';
import {
  ButtonIcon,
  Gray10,
  Gray100,
  Gray20,
  Gray50,
  Loader,
  typeCompact10Medium,
  typeCompact12Medium,
} from '@traceair/tds';
import { trackAction } from '../../../../libs/analytics/ActionTracking';
import DocPlaceholderSmall from './DocPlaceholderSmall';

type FilePreviewProps = {
  fileName: string;
  fileSize: number;
  onDelete: () => void;
  file?: File;
  thumbnailUrl?: string;
  downloadUrl?: string;
  isUploading?: boolean;
};

const FilePreview: FC<FilePreviewProps> = ({
  fileName,
  fileSize,
  onDelete,
  file,
  thumbnailUrl,
  downloadUrl,
  isUploading,
}) => {
  const [preview, setPreview] = useState<string | undefined>(thumbnailUrl);

  async function handleDownloadClick() {
    trackAction('Download document file button click', null);

    if (downloadUrl) {
      const attachment = await fetch(downloadUrl);
      const attachmentBlob = await attachment.blob();
      const localAttachmentUrl = URL.createObjectURL(attachmentBlob);

      const link = document.createElement('a');

      link.href = localAttachmentUrl;
      link.download = fileName;
      link.click();
      // free memory
      URL.revokeObjectURL(localAttachmentUrl);
    }
  }

  useEffect(() => {
    if (file && file.type.includes('image')) {
      const objectUrl = URL.createObjectURL(file);

      setPreview(objectUrl);

      // free memory on unmount
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [file]);

  return (
    <div className={classes(css.commonRoot, isUploading ? css.uploadingRoot : css.normalRoot)}>
      {isUploading ? (
        <>
          <div className={css.uploadingButtonIcon} onClick={onDelete}>
            <CloseIcon color={Gray50} />
          </div>
          <Loader />
          <div className={classes(typeCompact12Medium, css.uploadingSize)}>{`Uploading...${getFileSizeStr(
            fileSize,
          )}`}</div>
          <div className={classes(typeCompact10Medium, css.uploadingName)}>{fileName}</div>
        </>
      ) : (
        <>
          {/* Note the reverse order of blocks inside the root. The trick lets Info container occupy necessary space at the bottom */}
          <div className={css.buttonsContainer}>
            {downloadUrl && (
              <ButtonIcon
                style="tertiary"
                onClick={handleDownloadClick}
                icon={<DownloadIcon color={Gray100} />}
                size="xs"
              />
            )}
            <ButtonIcon
              type="black"
              style="tertiary"
              onClick={onDelete}
              icon={<DeleteIcon color={Gray100} />}
              size="xs"
            />
          </div>
          <div className={classes(typeCompact10Medium, css.infoContainer)}>
            <div>{fileName}</div>
            <div>{getFileSizeStr(fileSize)}</div>
          </div>
          <div className={css.pictureWrap}>
            {preview && <img src={preview} alt="preview" style={{ width: '100%' }} />}
            {!preview && (
              <div className={css.placeholderContainer}>
                <DocPlaceholderSmall />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const css = stylesheet({
  commonRoot: {
    width: 140,
    height: 140,
    boxSizing: 'border-box',
    borderRadius: 12,
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
  },
  uploadingRoot: {
    backgroundColor: Gray10,
    padding: '16px 8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadingSize: {
    marginTop: 6,
  },
  uploadingName: {
    marginTop: 4,
    color: Gray100,
    wordBreak: 'break-all',
    textAlign: 'center',
  },
  normalRoot: {
    border: `solid 1px ${Gray20}`,
    display: 'flex',
    flexDirection: 'column-reverse',
    justifyContent: 'space-between',
  },
  uploadingButtonIcon: {
    position: 'absolute',
    top: 7,
    right: 8,
    cursor: 'pointer',
  },
  infoContainer: {
    padding: 6,
    borderTop: `solid 1px ${Gray20}`,
    textAlign: 'center',
    wordBreak: 'break-all',
  },
  buttonsContainer: {
    position: 'absolute',
    top: 5,
    right: 3,
    display: 'flex',
    gap: 3,
  },
  pictureWrap: {
    maxHeight: '100%',
    overflow: 'hidden',
  },
  placeholderContainer: {
    marginTop: 30,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
});

function getFileSizeStr(fileSizeBytes: number) {
  // up to 100B - output in bytes
  if (fileSizeBytes < 100) {
    return `${fileSizeBytes} B`;
  }

  // up to 10KB - output in KBytes up to 2 decimal
  if (fileSizeBytes < 10000) {
    return `${Math.round((fileSizeBytes / 1000) * 100) / 100} KB`;
  }

  // up to 100KB - output in KBytes up to 1 decimal
  if (fileSizeBytes < 100000) {
    return `${Math.round((fileSizeBytes / 1000) * 10) / 10} KB`;
  }

  // output 18.9MB - output in Bytes up to 1 decimal
  return `${Math.round((fileSizeBytes / 1000000) * 100) / 100} MB`;
}

export default FilePreview;
